import { FC } from "react";
import { Box, CircularProgress, Divider, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "@/components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
} from "@/shared/components/header-items/HeaderItems";
import DashboardContentLayout from "@/shared/layouts/DashboardContentLayout";
import EmptyState from "@/shared/components/EmptyState";
import { AdRoutes } from "@/constants/ad-routes";
import useOrganizationDetail from "../hooks/useOrganizationDetail";
import useOrganizationAccessToggle from "../hooks/useOrganizationAccessToggle";
import useDeleteOrganization from "../hooks/useDeleteOrganization";
import OrganizationsListHeader from "../components/OrganizationsListHeader";
import OrganizationCard from "../components/OrganizationCard";
import OrganizationAccessConfirmDialog from "../components/OrganizationAccessConfirmDialog";
import OrganizationDeleteConfirmDialog from "../components/OrganizationDeleteConfirmDialog";
import TeamMembersSection from "../components/TeamMembersSection";

const OrganizationDetailPage: FC = () => {
  const navigate = useNavigate();
  const { organizationId = "" } = useParams<{ organizationId: string }>();
  const { organization, isLoading, isError, refetch } =
    useOrganizationDetail(organizationId);
  const {
    pendingOrganization,
    isSubmitting,
    requestToggle,
    confirmToggle,
    cancelToggle,
  } = useOrganizationAccessToggle();

  const {
    pendingOrganization: organizationPendingDelete,
    isSubmitting: isDeleting,
    requestDelete,
    confirmDelete,
    cancelDelete,
  } = useDeleteOrganization(() => navigate(AdRoutes.organizations));

  const renderContent = () => {
    if (isError) {
      return (
        <Box mt="20px">
          <EmptyState
            title="Couldn't load organization"
            description="Something went wrong while loading this organization. Please try again."
            actionLabel="Retry"
            onAction={() => refetch()}
          />
        </Box>
      );
    }

    if (isLoading || !organization) {
      return (
        <Box mt="40px" display="flex" justifyContent="center">
          <CircularProgress />
        </Box>
      );
    }

    return (
      <>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "24px",
            lineHeight: "32px",
            mb: "16px",
          }}
        >
          Organization Info
        </Typography>
        <OrganizationsListHeader sortable={false} />
        <OrganizationCard
          organization={organization}
          onToggleAccessRequest={requestToggle}
          onEditRequest={(org) =>
            navigate(
              AdRoutes.editOrganization.replace(":organizationId", org.id),
            )
          }
          onDeleteRequest={requestDelete}
        />
        <Divider sx={{ my: "32px" }} />
        <TeamMembersSection
          organizationId={organizationId}
          isOrganizationActive={organization.isActive}
        />
      </>
    );
  };

  return (
    <DashboardContentLayout horizontalPadding="40px">
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              { label: "Manage Organizations", url: AdRoutes.organizations },
              { label: organization?.name ?? "" },
            ]}
          />
        </HeaderItems>
      </Navbar>
      <Box mt="24px">{renderContent()}</Box>
      <OrganizationAccessConfirmDialog
        organization={pendingOrganization}
        isSubmitting={isSubmitting}
        onConfirm={confirmToggle}
        onCancel={cancelToggle}
      />
      <OrganizationDeleteConfirmDialog
        organization={organizationPendingDelete}
        isSubmitting={isDeleting}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </DashboardContentLayout>
  );
};

export default OrganizationDetailPage;
